import type {
  EstimateParticipationSubsidiesData,
  ParticipationSubsidyEstimates,
  PrestationAccountState,
  PrestationParticipationsFilters,
  PrestationParticipationWithEmployee,
  PrestationsFilters,
  SubsidisingRuleQuery,
} from '@cpce/shared';
import type { SubsidyProduct, SubsidyRule } from '@cpce/ticketing-api';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiPost } from '@/api/api.utils';
import { commiteeInfiniteQuery, commiteeQuery } from '@/api/queries.utils';
import { formatPrestationParticipation } from '@/features/beneficiaries/employees/debts/utils/debts.utils';
import { useCommiteeIdFromUrl } from '@/utils/router';
import { prestationsQueryKeys } from './prestations.query-keys';
import type {
  Prestation,
  PrestationDetail,
  PrestationParticipationFormatted,
} from './prestations.types';

export function usePrestationsInfinite(
  params: Pick<
    PrestationsFilters,
    'accountingCategoryId' | 'supplierId' | 'availableOnly' | 'includeArchived'
  > & {
    search?: string;
  } = {}
) {
  return commiteeInfiniteQuery<Prestation>({
    queryKey: prestationsQueryKeys.infinite(params),
    url: '/commitee/:commiteeId/prestations',
    params,
  });
}

export function usePrestation(id: number) {
  return commiteeQuery<PrestationDetail>({
    queryKey: prestationsQueryKeys.one(id),
    url: `/commitee/:commiteeId/prestations/${id}`,
    enabled: id > 0,
  });
}

export function usePrestationsParticipations(
  filters: PrestationParticipationsFilters = {},
  options?: { enabled?: boolean }
) {
  return commiteeQuery<PrestationParticipationWithEmployee[], PrestationParticipationFormatted[]>({
    queryKey: prestationsQueryKeys.participations(filters),
    url: '/commitee/:commiteeId/prestations/participations',
    transform: (data) => data.map(formatPrestationParticipation),
    params: filters,
    enabled: options?.enabled,
  });
}

export function usePrestationParticipation(id: number) {
  return commiteeQuery<PrestationParticipationWithEmployee, PrestationParticipationFormatted>({
    queryKey: prestationsQueryKeys.participation(id),
    url: `/commitee/:commiteeId/prestations/participations/${id}`,
    transform: formatPrestationParticipation,
    enabled: id > 0,
    refetchOnFocusOrReconnect: false,
  });
}

export function useSubsidyRules() {
  return commiteeQuery<SubsidyRule[]>({
    queryKey: prestationsQueryKeys.subsidyRules,
    url: '/commitee/:commiteeId/prestations/subsidy-rules',
  });
}

export function usePrestationSubsidyRules(id: number) {
  return commiteeQuery<SubsidyRule[]>({
    queryKey: prestationsQueryKeys.prestationSubsidyRules(id),
    url: `/commitee/:commiteeId/prestations/${id}/subsidy-rules`,
    enabled: id > 0,
  });
}

/** The billetterie rule a target's subsidy comes from; null query while no target is picked. */
export function useSubsidisingRule(query: SubsidisingRuleQuery | null) {
  return commiteeQuery<{ rule: SubsidyRule | null }>({
    queryKey: prestationsQueryKeys.subsidisingRule(query ?? {}),
    url: '/commitee/:commiteeId/prestations/subsidising-rule',
    params: query ?? undefined,
    enabled: query !== null,
  });
}

export function usePrestationAccount(prestationId: number) {
  return commiteeQuery<PrestationAccountState>({
    queryKey: prestationsQueryKeys.account(prestationId),
    url: `/commitee/:commiteeId/prestations/${prestationId}/account`,
    enabled: prestationId > 0,
  });
}

export function useSubsidyProducts() {
  return commiteeQuery<SubsidyProduct[]>({
    queryKey: prestationsQueryKeys.subsidyProducts,
    url: '/commitee/:commiteeId/prestations/subsidy-products',
  });
}

const NO_ESTIMATE_REQUEST: EstimateParticipationSubsidiesData = {
  prestationId: 0,
  participants: [],
};

// Not `commiteeQuery`: the whole selection is POSTed (thousands of lines on a big CSE), and a line
// the billetterie fails to estimate is an answer shown in its row, not a toast. The previous
// answer stays on screen while a tarif or quantity change is re-estimated.
export function useParticipationSubsidyEstimates(data: EstimateParticipationSubsidiesData | null) {
  const commiteeId = useCommiteeIdFromUrl();
  return useQuery({
    queryKey: [...prestationsQueryKeys.subsidyEstimates(data ?? NO_ESTIMATE_REQUEST), commiteeId],
    queryFn: () =>
      apiPost<ParticipationSubsidyEstimates>(
        `/commitee/${commiteeId}/prestations/participations/estimate`,
        data ?? NO_ESTIMATE_REQUEST
      ),
    enabled: data !== null && data.participants.length > 0 && commiteeId !== undefined,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}
